/**
 * DownloadPanel — browse and download aviation data from the STAC download API.
 *
 * Lists available cycle data packages (28-day AIRAC, 56-day, non-cycle) with
 * collection filters and direct download links. When the AWS backend is
 * unavailable the panel shows a clear offline notice rather than a bare error.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { getCurrentCycle, getCycleWarning } from '../services/ChartCycleService';
import {
  formatFileSize,
  getStacCollections,
  getPrimaryAssetUrl,
  itemTitle,
  searchStacItems,
  type StacCollection,
  type StacItem,
} from '../services/StacDownloadService';

const PAGE_SIZE = 20;

function CycleBadge() {
  const cycle = getCurrentCycle();
  const warning = getCycleWarning(cycle);
  const color = warning.level === 'ok'
    ? 'var(--success, #22C55E)'
    : warning.level === 'warning'
      ? 'var(--warning, #F59E0B)'
      : 'var(--danger, #EF4444)';
  return (
    <span style={{ color, fontSize: 11, marginLeft: 6 }}>{warning.message}</span>
  );
}

function ItemRow({ item }: { item: StacItem }) {
  const url = getPrimaryAssetUrl(item);
  const size = formatFileSize(item.properties.file_size_bytes as number | undefined);
  const dataType = item.properties.data_type as string | undefined;
  const cycle = item.properties.cycle as string | undefined;

  return (
    <div className="data-row" style={{ alignItems: 'flex-start', gap: 8 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, overflowWrap: 'anywhere' }}>{itemTitle(item)}</div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, display: 'flex', gap: 8 }}>
          {cycle && <span>Cycle {cycle}</span>}
          {dataType && <span>{dataType}</span>}
          {size && <span>{size}</span>}
        </div>
      </div>
      {url ? (
        <a
          href={url}
          download
          className="btn btn-secondary"
          style={{ fontSize: 11, padding: '3px 8px', flexShrink: 0 }}
        >
          Download
        </a>
      ) : (
        <span style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0 }}>No asset</span>
      )}
    </div>
  );
}

export const DownloadPanel: React.FC = () => {
  const [collections, setCollections] = useState<StacCollection[]>([]);
  const [selectedCollection, setSelectedCollection] = useState('');
  const [items, setItems] = useState<StacItem[]>([]);
  const [totalMatched, setTotalMatched] = useState<number | null>(null);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [collectionsError, setCollectionsError] = useState<string | null>(null);

  // Load collections once on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cols = await getStacCollections();
        if (!cancelled) setCollections(cols);
      } catch (e) {
        if (!cancelled) {
          setCollectionsError(e instanceof Error ? e.message : 'Could not load collections');
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const search = useCallback(async (page: number, collection: string) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        ...(collection ? { collections: [collection] } : {}),
      };
      const result = await searchStacItems(params);
      setItems(result.features);
      const matched =
        result.numberMatched ??
        result.context?.matched ??
        null;
      setTotalMatched(matched);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Download catalog is unavailable');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-search when collection filter or page changes
  useEffect(() => {
    setOffset(0);
  }, [selectedCollection]);

  useEffect(() => {
    void search(offset / PAGE_SIZE, selectedCollection);
  }, [search, offset, selectedCollection]);

  const isServiceDown =
    error?.includes('503') ||
    error?.includes('502') ||
    error?.includes('unavailable') ||
    error?.includes('Failed to fetch');

  return (
    <div>
      {/* Cycle banner */}
      <div className="panel-section" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Current AIRAC</span>
        <CycleBadge />
      </div>

      {/* Collection filter */}
      <div className="panel-section">
        <div className="panel-section-title">Collection</div>
        {collectionsError ? (
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Collections unavailable</div>
        ) : (
          <select
            className="fp-field"
            value={selectedCollection}
            onChange={(e) => setSelectedCollection(e.target.value)}
            style={{ width: '100%', padding: '4px 6px', fontSize: 12, background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 4 }}
          >
            <option value="">All collections ({collections.length || '…'})</option>
            {collections.map((c) => (
              <option key={c.id} value={c.id}>{c.title ?? c.id}</option>
            ))}
          </select>
        )}
      </div>

      {/* Status line */}
      {isServiceDown ? (
        <div className="panel-section">
          <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, padding: '10px 12px', fontSize: 12 }}>
            <div style={{ fontWeight: 600, color: 'var(--danger, #EF4444)', marginBottom: 4 }}>Download service unavailable</div>
            <div style={{ color: 'var(--text-secondary)' }}>
              The AWS download backend is temporarily unreachable. Aviation data packages will appear here once the service is restored.
            </div>
          </div>
        </div>
      ) : error ? (
        <div className="panel-error">{error}</div>
      ) : null}

      {/* Results */}
      {!isServiceDown && (
        <div className="panel-section">
          <div className="panel-section-title" style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Packages</span>
            {totalMatched !== null && (
              <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>{totalMatched.toLocaleString()} total</span>
            )}
          </div>

          {loading && (
            <div style={{ color: 'var(--text-muted)', fontSize: 12, padding: '8px 0' }}>Loading…</div>
          )}

          {!loading && items.length === 0 && !error && (
            <div style={{ color: 'var(--text-muted)', fontSize: 12, padding: '8px 0' }}>No packages found.</div>
          )}

          {items.map((item) => <ItemRow key={item.id} item={item} />)}
        </div>
      )}

      {/* Pagination */}
      {!isServiceDown && !loading && items.length > 0 && (
        <div className="panel-section" style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
          <button
            className="btn btn-secondary"
            disabled={offset === 0}
            onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
            style={{ fontSize: 11 }}
          >
            ← Prev
          </button>
          <span style={{ fontSize: 11, color: 'var(--text-muted)', alignSelf: 'center' }}>
            {offset + 1}–{offset + items.length}
            {totalMatched !== null ? ` of ${totalMatched.toLocaleString()}` : ''}
          </span>
          <button
            className="btn btn-secondary"
            disabled={items.length < PAGE_SIZE}
            onClick={() => setOffset((o) => o + PAGE_SIZE)}
            style={{ fontSize: 11 }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};
